/**
 * HighlightJargon — scan a text string for known glossary terms and wrap
 * each match in a <Jargon> popover component.
 *
 * Multi-word terms are prioritised over single words (see
 * GLOSSARY_TERMS_ORDERED in ../data/playbookGlossary). Matching is
 * case-insensitive; the original casing in the source string is preserved
 * in the rendered output.
 *
 * Usage:
 *   <HighlightJargon text="Install GA4 and complete the 301 map." />
 */
import { Jargon } from './Jargon';
import { GLOSSARY, GLOSSARY_TERMS_ORDERED } from '../data/playbookGlossary';

// Build one giant regex once. `\b` won't work reliably around punctuation
// like "→" or "·", so we use a lookaround-free approach: match the term
// preceded by start-of-string / whitespace / punctuation and followed by
// the same, so we don't accidentally match "cms" inside "cmscribe".
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const TERMS_REGEX = new RegExp(
  '(^|[\\s\\p{P}\\p{S}])(' +
    GLOSSARY_TERMS_ORDERED.map(escapeRegex).join('|') +
    ')(?=$|[\\s\\p{P}\\p{S}])',
  'giu'
);

export function HighlightJargon({ text, className }) {
  if (!text || typeof text !== 'string') {
    return <span className={className}>{text}</span>;
  }

  const nodes = [];
  let lastIndex = 0;
  let m;
  let key = 0;
  // Reset regex state — it's stateful because of the `g` flag.
  TERMS_REGEX.lastIndex = 0;

  while ((m = TERMS_REGEX.exec(text)) !== null) {
    const [, leading, matched] = m;
    const matchStart = m.index + leading.length;
    const matchEnd = matchStart + matched.length;

    if (matchStart > lastIndex) {
      nodes.push(text.slice(lastIndex, matchStart));
    }

    const entry = GLOSSARY[matched.toLowerCase()];
    if (entry) {
      nodes.push(
        <Jargon key={`j-${key++}-${matchStart}`} term={entry}>
          {matched}
        </Jargon>
      );
    } else {
      nodes.push(matched);
    }

    lastIndex = matchEnd;

    // Guard against zero-width matches
    if (m.index === TERMS_REGEX.lastIndex) TERMS_REGEX.lastIndex++;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

  return <span className={className}>{nodes}</span>;
}

export default HighlightJargon;
